// Per-row action cluster on Shared links (architecture sprint-06 §6.3, F70/F73–F76). Buttons come from
// the shared state machine: active → Copy + ⋯ (Extend, Make public/private, Revoke); expired → Extend…;
// revoked → nothing. The server is authoritative: a 409 SHARE_LINK_STATE_CHANGED closes the dialog,
// toasts the server message and refetches the list.
import { canDo, publicConfirmText } from '@shared/share-link-rules';
import type { LocalDate } from '@shared/calendar';
import { Copy, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SHARE_LINKS_KEY, useExtendShareLink, useRevokeShareLink, useSetShareLinkVisibility } from '@/hooks/use-share-links';
import { ApiClientError } from '@/lib/api';
import { formatRange } from '@/lib/format';
import { GENERIC_ERROR_MESSAGE, handleGlobalError } from '@/lib/query-client';
import type { ShareLink } from '@/types/admin';
import { ExtendExpiryDialog } from './ExtendExpiryDialog';

const STATE_CHANGED_CODE = 'SHARE_LINK_STATE_CHANGED';
const SESSION_CODES = new Set(['ACCOUNT_DEACTIVATED', 'SESSION_EXPIRED']);
export const REVOKE_BODY =
  'Anyone who opens it will see “This link is no longer available”. This can’t be undone; create a new link from the Team report if you need one again.';

type OpenDialog = 'extend' | 'public' | 'revoke' | null;

interface ShareLinkActionsProps {
  link: ShareLink;
  today: LocalDate;
  timezone: string;
  copyPending: boolean;
  onCopy: (link: ShareLink) => void;
}

export function ShareLinkActions({ link, today, timezone, copyPending, onCopy }: ShareLinkActionsProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const extend = useExtendShareLink();
  const visibility = useSetShareLinkVisibility();
  const revoke = useRevokeShareLink();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const range = formatRange(link.from, link.to);

  /** 409 → close, toast, refetch; session errors → global path; anything else → toast (dialog stays open). */
  function onActionError(error: Error): void {
    if (error instanceof ApiClientError && error.code === STATE_CHANGED_CODE) {
      setDialog(null);
      toast.error(error.message);
      void queryClient.invalidateQueries({ queryKey: SHARE_LINKS_KEY });
      return;
    }
    if (error instanceof ApiClientError && SESSION_CODES.has(error.code)) {
      handleGlobalError(error, undefined);
      return;
    }
    // Other API errors (e.g. EXPIRY_IN_PAST) keep the dialog open so the Admin can correct the input.
    toast.error(error instanceof ApiClientError ? error.message : GENERIC_ERROR_MESSAGE);
  }

  function submitExtend(expiresOn: LocalDate): void {
    const reviving = link.status === 'expired';
    extend.mutate(
      { id: link.id, expiresOn },
      {
        onSuccess: () => {
          setDialog(null);
          toast.success(reviving ? 'Link reactivated.' : 'Expiry updated.');
        },
        onError: onActionError,
      },
    );
  }

  function makePublic(): void {
    visibility.mutate(
      { id: link.id, change: { visibility: 'public', confirm: true } },
      {
        onSuccess: () => {
          setDialog(null);
          toast.success('Link is now public.');
        },
        onError: onActionError,
      },
    );
  }

  function makePrivate(): void {
    visibility.mutate(
      { id: link.id, change: { visibility: 'sign_in_required' } },
      { onSuccess: () => toast.success('Link now needs sign-in.'), onError: onActionError },
    );
  }

  function submitRevoke(): void {
    revoke.mutate(link.id, {
      onSuccess: () => {
        setDialog(null);
        toast.success('Link revoked.');
      },
      onError: onActionError,
    });
  }

  if (link.status === 'revoked') return null;

  const dialogs = (
    <>
      <ExtendExpiryDialog
        open={dialog === 'extend'}
        onOpenChange={(open) => setDialog(open ? 'extend' : null)}
        expired={link.status === 'expired'}
        today={today}
        timezone={timezone}
        pending={extend.isPending}
        onSubmit={submitExtend}
      />
      <ConfirmDialog
        open={dialog === 'public'}
        onOpenChange={(open) => setDialog(open ? 'public' : null)}
        title="Make this link public?"
        body={publicConfirmText(range)}
        confirmLabel="Make public"
        destructive
        pending={visibility.isPending}
        onConfirm={makePublic}
      />
      <ConfirmDialog
        open={dialog === 'revoke'}
        onOpenChange={(open) => setDialog(open ? 'revoke' : null)}
        title="Revoke this link?"
        body={REVOKE_BODY}
        confirmLabel="Revoke"
        destructive
        pending={revoke.isPending}
        onConfirm={submitRevoke}
      />
    </>
  );

  if (!canDo(link.status, 'copy')) {
    return (
      <div className="flex justify-end gap-2">
        {canDo(link.status, 'extend') ? (
          <Button variant="outline" size="xs" onClick={() => setDialog('extend')}>
            Extend…
          </Button>
        ) : null}
        {dialogs}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Button variant="outline" size="xs" disabled={copyPending} onClick={() => onCopy(link)}>
        <Copy aria-hidden="true" />
        Copy link
      </Button>
      {/* modal={false}: a modal menu that opens a Dialog from onSelect can leave pointer-events: none on <body>. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`More actions for link ${range}`} disabled={visibility.isPending}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setDialog('extend')}>Extend expiry…</DropdownMenuItem>
          {link.visibility === 'public' ? (
            <DropdownMenuItem onSelect={makePrivate}>Make private</DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => setDialog('public')}>Make public…</DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setDialog('revoke')}>Revoke…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </div>
  );
}
